/**
 * OF-001D — Phase C — Route ↔ Workspace consistency keeper.
 *
 * Mounted once inside <HashRouter>. Keeps the Header label
 * (`activeWorkspace`) and the rendered page (the URL) from contradicting
 * each other. Renders nothing.
 *
 * TREATX-UAT-PROD-CONTEXT-SURFACE-01d (Codex review #3, BLOCKER 1)
 * ---------------------------------------------------------------
 * This component used to call `setActiveWorkspace(owner.workspace_id)`
 * for ANY workspace that "owned" the current `default_route`, which ends
 * in `POST /api/auth/unified/context` — i.e. the URL was an independent
 * automatic context-selection authority. A URL is NAVIGATION intent; it
 * is NOT authorization-context intent when several legitimate contexts
 * exist (that is how a Provider A + Provider B account silently got
 * Provider A, and how a deep link could pick a persona for a mixed
 * account).
 *
 * The URL may therefore no longer select a context. The ONLY automatic
 * selection permitted anywhere in the frontend is the shared
 * `resolveUnambiguousWorkspace` rule; this component may act only when
 * that rule PROVES the account has exactly one possible selectable
 * context and the requested route belongs to it (a decision identical to
 * the `RBACContext` bootstrap, so the two can never diverge). In every
 * ambiguous case it does nothing and the shared admission/recovery
 * selector resolves the ambiguity. Once a valid context exists, ordinary
 * navigation continues untouched — no redirects, no route aliases, no
 * deep-link breakage.
 */
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useRBAC } from '../context/RBACContext';
import { findWorkspaceOwningRoute } from '../utils/dashboardRouteResolver';
import { resolveUnambiguousWorkspace } from '../utils/workspaceSelection';

const WorkspaceRouteSync = () => {
  const location = useLocation();
  const {
    workspaces, roleAssignments, activeWorkspace, profileLoaded, setActiveWorkspace,
  } = useRBAC();

  useEffect(() => {
    // TREATX-UAT-PROD-ADMISSION-RACE-01 — readiness ordering only.
    // While the authoritative server state has not been applied
    // (`profileLoaded === false`, e.g. the initial /me is still in flight
    // or a canonical context invalidation is being reconciled) this effect
    // must not propose anything: its inputs are the CACHED catalogue, and
    // proposing from them re-mints a context and remounts the protected
    // child before reconciliation completes. Route sync is NOT retired —
    // after readiness it behaves exactly as before.
    if (!profileLoaded) return;
    if (!Array.isArray(workspaces) || workspaces.length === 0) return;
    const owner = findWorkspaceOwningRoute(location.pathname, workspaces);
    if (!owner) return;
    if (activeWorkspace && owner.workspace_id === activeWorkspace.workspace_id) return;
    // Shared unambiguous rule ONLY — never route-ownership inference.
    const sole = resolveUnambiguousWorkspace(roleAssignments, workspaces);
    if (!sole || sole.workspace_id !== owner.workspace_id) return;
    setActiveWorkspace(sole.workspace_id);
  }, [location.pathname, workspaces, roleAssignments, activeWorkspace, profileLoaded,
      setActiveWorkspace]);

  return null;
};

export default WorkspaceRouteSync;
